import React, { useEffect, useMemo, useState } from 'react';
import { Alert, Button, Form, Modal, Spinner } from 'react-bootstrap';
import { RoomType } from '../../types/Enums';
import { translate } from '../../types/EnumTranslations';
import { RoomCreateInput } from '../../api/roomsApi';

interface RoomCreateModalProps {
    show: boolean;
    onHide: () => void;
    /** Level chosen in the area form; fixed here. */
    levelKey: string;
    levelName: string;
    /** Creates the room (writes the node, refetches the building) and returns its key. */
    onSubmit: (input: RoomCreateInput) => Promise<string>;
    /** Called with the new room's key after a successful save. */
    onCreated: (roomKey: string) => void;
    submitting?: boolean;
}

/**
 * "Skapa rum" from the parking area form: a room on the chosen level
 * with optional number and label, type preselected to garage. Placing the room
 * on the drawing is done later in the inventory.
 */
const RoomCreateModal: React.FC<RoomCreateModalProps> = ({
    show, onHide, levelKey, levelName, onSubmit, onCreated, submitting = false,
}) => {
    const [number, setNumber] = useState('');
    const [label, setLabel] = useState('');
    const [type, setType] = useState<RoomType>(RoomType.RoomGarage);
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        if (!show) return;
        setNumber('');
        setLabel('');
        setType(RoomType.RoomGarage);
        setError(null);
    }, [show]);

    // Garage first, then the rest alphabetically by Swedish name.
    const roomTypes = useMemo(() => Object.values(RoomType)
        .filter(value => value !== RoomType.RoomGarage)
        .sort((a, b) => translate(a).localeCompare(translate(b), 'sv')), []);

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        e.stopPropagation();
        setError(null);
        try {
            const key = await onSubmit({
                levelKey,
                type,
                number: number.trim() || undefined,
                label: label.trim() || undefined,
            });
            onCreated(key);
        } catch (err) {
            setError(err instanceof Error ? err.message : 'Kunde inte skapa rummet');
        }
    };

    return (
        <Modal show={show} onHide={onHide} centered>
            <Form onSubmit={handleSubmit}>
                <Modal.Header closeButton>
                    <Modal.Title>Skapa rum</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    {error && <Alert variant="danger" onClose={() => setError(null)} dismissible>{error}</Alert>}

                    <Form.Group className="mb-3">
                        <Form.Label>Våning</Form.Label>
                        <Form.Control type="text" value={levelName} readOnly disabled />
                    </Form.Group>

                    <Form.Group className="mb-3">
                        <Form.Label>Rumsnummer</Form.Label>
                        <Form.Control
                            type="text"
                            value={number}
                            onChange={e => setNumber(e.target.value)}
                            placeholder="Valfritt"
                            autoFocus
                        />
                    </Form.Group>

                    <Form.Group className="mb-3">
                        <Form.Label>Namn</Form.Label>
                        <Form.Control
                            type="text"
                            value={label}
                            onChange={e => setLabel(e.target.value)}
                            placeholder="T.ex. Garage norr"
                        />
                    </Form.Group>

                    <Form.Group className="mb-1">
                        <Form.Label>Typ av rum</Form.Label>
                        <Form.Select value={type} onChange={e => setType(e.target.value as RoomType)}>
                            <option value={RoomType.RoomGarage}>{translate(RoomType.RoomGarage)}</option>
                            {roomTypes.map(value => (
                                <option key={value} value={value}>{translate(value)}</option>
                            ))}
                        </Form.Select>
                        <Form.Text muted>Rummet placeras på ritningen senare, i inventeringen.</Form.Text>
                    </Form.Group>
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={onHide} disabled={submitting}>Avbryt</Button>
                    <Button type="submit" variant="primary" disabled={submitting}>
                        {submitting ? <><Spinner size="sm" animation="border" /> Sparar...</> : 'Skapa rum'}
                    </Button>
                </Modal.Footer>
            </Form>
        </Modal>
    );
};

export default RoomCreateModal;
